import { Fragment, useMemo, useState, useEffect } from "react";
import type { CSSProperties, SVGProps } from "react";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  PieChart,
  Pie,
  Cell,
  Label,
  LabelList,
  ScatterChart,
  Scatter,
  ReferenceLine,
} from "recharts";

import type { DatasetDashboardType, FiguresData, ShipmentSummary } from "../types";
import { formatDateLabel, formatTooltipDate } from "../lib/chartUtils";
import { BRAND_COLORS, hexToRgba } from "../lib/brandColors";

type KeyFiguresPanelProps = {
  figures: FiguresData;
  dashboardType?: DatasetDashboardType;
  shipments?: ShipmentSummary[];
  safeRange?: { min: number; max: number } | null;
  yAxisDomain?: readonly [number, number] | null;
  hasBoxes?: boolean;
};

type HeatmapMetricKey = (typeof HEATMAP_METRICS)[number]["key"];

type HeatmapRow = {
  destination: string;
  values: Record<HeatmapMetricKey, number | null>;
};

type HeatmapDataset = {
  rows: HeatmapRow[];
  domain: [number, number] | null;
};

type SensorHeatmapCell = FiguresData["heatmapInside"][number];
type ExcursionYAxisMode = "min" | "max";

const COMPLIANCE_COLORS = ["#22c55e", "#ef4444"];
const RECOVERY_COLORS = [BRAND_COLORS.blueLight, BRAND_COLORS.orange];
const LAINPHARMA_BAR_BLUE = BRAND_COLORS.blueLight;
const EXCURSION_BAR_ORANGE = BRAND_COLORS.orange;

const HEATMAP_METRICS = [
  { key: "min", label: "Min" },
  { key: "mean", label: "Moyenne" },
  { key: "max", label: "Max" },
] as const;

type ExcursionScatterCategory = "none" | "cold" | "hot" | "mixed";

type ExcursionScatterPoint = {
  packageLabel: string;
  packageType: string;
  packageShape: PackageShape;
  durationHours: number;
  plotTemperature: number;
  minValue: number | null;
  maxValue: number | null;
  category: ExcursionScatterCategory;
};

type PackageShape = "circle" | "square" | "diamond" | "triangle" | "cross" | "star";

const EXCURSION_SCATTER_COLORS: Record<ExcursionScatterCategory, string> = {
  none: "#6FCFC0",
  cold: "#4F6FD1",
  hot: "#FF7A66",
  mixed: "#B25AE6",
};

const EXCURSION_SCATTER_LABELS: Record<ExcursionScatterCategory, string> = {
  none: "No excursion",
  cold: "Cold excursion",
  hot: "Hot excursion",
  mixed: "Mixed excursion",
};

const PACKAGE_SHAPES: PackageShape[] = ["circle", "square", "diamond", "triangle", "cross", "star"];

export function KeyFiguresPanel({
  figures,
  dashboardType = "operations",
  shipments = [],
  safeRange = null,
  yAxisDomain = null,
  hasBoxes = false,
}: KeyFiguresPanelProps) {
  const [excursionYAxisMode, setExcursionYAxisMode] = useState<ExcursionYAxisMode>("min");
  const isTemperatureDashboard = dashboardType === "temperature";
  const insidePieData = useMemo(
    () => [
      { name: "Compliant", value: figures.complianceInside.yes },
      { name: "Non-compliant", value: figures.complianceInside.no },
    ],
    [figures.complianceInside],
  );

  const outsidePieData = useMemo(
    () => [
      { name: "Compliant", value: figures.complianceOutside.yes },
      { name: "Non-compliant", value: figures.complianceOutside.no },
    ],
    [figures.complianceOutside],
  );

  const recoveryPieData = useMemo(() => {
    const shipped = figures.recovery.shipped ?? 0;
    const recovered = figures.recovery.recovered ?? 0;
    const pending = Math.max(shipped - recovered, 0);
    return [
      { name: "Collected", value: recovered },
      { name: "Not collected", value: pending },
    ];
  }, [figures.recovery]);

  const insideTotal = figures.complianceInside.yes + figures.complianceInside.no;
  const outsideTotal = figures.complianceOutside.yes + figures.complianceOutside.no;
  const shippedTotal = figures.recovery.shipped ?? 0;
  const hasInsidePie = insideTotal > 0;
  const hasOutsidePie = outsideTotal > 0;
  const hasRecoveryPie = recoveryPieData.some((slice) => slice.value > 0);
  const showComplianceSection = hasInsidePie || hasOutsidePie || hasRecoveryPie;

  const insideRate = ratioToPercent(figures.complianceInside.yes, insideTotal);
  const outsideRate = ratioToPercent(figures.complianceOutside.yes, outsideTotal);
  const recoveryRate = ratioToPercent(figures.recovery.recovered, shippedTotal);

  const timelineData = useMemo(() => {
    return figures.timeline
      .slice(-12)
      .map((entry) => ({ date: entry.date, shipments: entry.shipments }))
      .sort((a, b) => {
        const timeA = new Date(a.date ?? "").getTime();
        const timeB = new Date(b.date ?? "").getTime();
        if (Number.isFinite(timeA) && Number.isFinite(timeB)) {
          return timeA - timeB;
        }
        return (a.date ?? "").localeCompare(b.date ?? "");
      });
  }, [figures.timeline]);

  const countriesChartData = useMemo(() => {
    return [...figures.countries]
      .sort((a, b) => b.shipments - a.shipments)
      .map((entry) => ({
        country: entry.country || "N/A",
        shipments: entry.shipments,
      }));
  }, [figures.countries]);

  const boxDistributionData = useMemo(() => {
    if (!hasBoxes) {
      return [];
    }
    return [...(figures.boxDistribution ?? [])]
      .filter((entry) => Boolean(entry.boxIdentifier))
      .sort((a, b) => b.shipments - a.shipments)
      .slice(0, 20)
      .map((entry) => ({
        boxIdentifier: entry.boxIdentifier,
        shipments: entry.shipments,
      }));
  }, [figures.boxDistribution, hasBoxes]);
  const boxExcursionRateData = useMemo(() => {
    if (!hasBoxes) {
      return [];
    }
    return [...(figures.boxExcursionRate ?? [])]
      .filter((entry) => Boolean(entry.boxIdentifier) && entry.shipments > 0)
      .sort((a, b) => b.excursionRate - a.excursionRate || b.shipments - a.shipments)
      .slice(0, 20);
  }, [figures.boxExcursionRate, hasBoxes]);
  const boxExcursionStackedData = useMemo(() => {
    const excursionByBox = new Map(
      boxExcursionRateData.map((entry) => [
        entry.boxIdentifier,
        {
          shipments: entry.shipments,
          withExcursion: entry.withExcursion,
          excursionRate: entry.excursionRate,
        },
      ]),
    );

    return boxDistributionData
      .map((entry) => {
        const excursion = excursionByBox.get(entry.boxIdentifier) ?? {
          shipments: entry.shipments,
          withExcursion: 0,
          excursionRate: 0,
        };
        const withExcursion = Math.min(excursion.withExcursion, entry.shipments);
        return {
          boxIdentifier: entry.boxIdentifier,
          shipments: entry.shipments,
          withExcursion,
          withoutExcursion: Math.max(entry.shipments - withExcursion, 0),
          excursionRate: excursion.excursionRate,
        };
      })
      .filter((entry) => entry.shipments > 0);
  }, [boxDistributionData, boxExcursionRateData]);

  const histogramData = figures.histogramHours ?? [];
  const hasTimelineChart = timelineData.some((entry) => entry.shipments > 0);
  const hasCountriesChart = countriesChartData.some((entry) => entry.shipments > 0);
  const hasHistogramChart = histogramData.some((entry) => entry.count > 0);
  const hasBoxDistributionChart = boxDistributionData.some((entry) => entry.shipments > 0);
  const hasBoxExcursionRateChart = boxExcursionRateData.some((entry) => entry.shipments > 0);
  const showTrendsSection =
    hasTimelineChart ||
    hasCountriesChart ||
    (isTemperatureDashboard && hasHistogramChart);

  const experienceHeatmap = useMemo(
    () => buildHeatmapDataset(figures.destinationExperience),
    [figures.destinationExperience],
  );

  const outsideTemperatureHeatmap = useMemo(
    () => buildHeatmapDataset(figures.outsideTemperature),
    [figures.outsideTemperature],
  );
  const hasSensorHeatmap = (figures.heatmapInside?.length ?? 0) > 0 || (figures.heatmapOutside?.length ?? 0) > 0;
  const hasExperienceHeatmap = experienceHeatmap.rows.length > 0 && Boolean(experienceHeatmap.domain);
  const hasOutsideTemperatureHeatmap =
    outsideTemperatureHeatmap.rows.length > 0 && Boolean(outsideTemperatureHeatmap.domain);
  const excursionScatter = useMemo(
    () => buildExcursionScatterData(shipments, safeRange, excursionYAxisMode, hasBoxes),
    [excursionYAxisMode, hasBoxes, safeRange, shipments],
  );
  const hasExcursionScatter = excursionScatter.points.length > 0;
  const excursionYDomain = useMemo(
    () => buildExcursionYAxisDomain(excursionScatter.points, safeRange, yAxisDomain),
    [excursionScatter.points, safeRange, yAxisDomain],
  );

  return (
    <div className="chart-wrapper" style={{ display: "grid", gap: "1.5rem" }}>
      {isTemperatureDashboard && showComplianceSection ? (
        <section className="chart-panel">
          <h3>Compliance Rate & Collection Rate</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "1.5rem" }}>
            {hasInsidePie ? (
              <PieBlock
                title="Inside sensors compliance rate"
                data={insidePieData}
                colors={COMPLIANCE_COLORS}
                focusIndex={0}
                percentage={insideRate}
                fraction={{ label: "Compliant", numerator: figures.complianceInside.yes, denominator: insideTotal }}
                showSliceLabels
              />
            ) : null}
            {hasOutsidePie ? (
              <PieBlock
                title="Outside sensors compliance rate"
                data={outsidePieData}
                colors={COMPLIANCE_COLORS}
                focusIndex={0}
                percentage={outsideRate}
                fraction={{ label: "Compliant", numerator: figures.complianceOutside.yes, denominator: outsideTotal }}
                showSliceLabels
              />
            ) : null}
            {hasRecoveryPie ? (
              <PieBlock
                title="Collection rate"
                data={recoveryPieData}
                colors={RECOVERY_COLORS}
                focusIndex={0}
                percentage={recoveryRate}
                fraction={{
                  label: "Collected",
                  numerator: figures.recovery.recovered ?? 0,
                  denominator: shippedTotal,
                }}
                showSliceLabels
              />
            ) : null}
          </div>
        </section>
      ) : null}

      {showTrendsSection ? (
        <section className="chart-panel">
          <h3>Trends and breakdowns</h3>
          <div style={{ display: "grid", gap: "1.5rem" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "1.5rem" }}>
              {hasTimelineChart ? (
                <div style={{ flex: "1 1 420px", minWidth: "320px" }}>
                  <h4 style={{ marginBottom: "0.5rem" }}>Expéditions (12 derniers points)</h4>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={timelineData} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" stroke="#475569" tickFormatter={formatDateLabel} />
                      <YAxis stroke="#475569" allowDecimals={false} />
                      <Tooltip labelFormatter={formatTooltipDate} />
                      <Bar dataKey="shipments" name="Expéditions" fill="#2563eb" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : null}

              {hasCountriesChart ? (
                <div style={{ flex: "1 1 420px", minWidth: "320px" }}>
                  <h4 style={{ marginBottom: "0.5rem" }}>Breakdown of final destinations</h4>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={countriesChartData}
                      layout="vertical"
                      margin={{ top: 8, right: 16, bottom: 28, left: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis
                        type="number"
                        stroke="#475569"
                        allowDecimals={false}
                        label={{ value: "Shipment quantity", position: "bottom", offset: 8, fill: "#475569" }}
                      />
                      <YAxis
                        type="category"
                        dataKey="country"
                        width={120}
                        stroke="#475569"
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip formatter={(value: number) => [`${value}`, "Shipment quantity"]} />
                      <Bar dataKey="shipments" name="Shipment quantity" fill="#00738C" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : null}
            </div>

            {isTemperatureDashboard && hasHistogramChart ? (
              <div style={{ minWidth: "320px" }}>
                <h4 style={{ marginBottom: "0.5rem" }}>Breakdown of total shipping durations</h4>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={histogramData} margin={{ top: 8, right: 16, bottom: 28, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="range"
                      stroke="#475569"
                      label={{ value: "Duration in hour", position: "bottom", offset: 8, fill: "#475569" }}
                    />
                    <YAxis stroke="#475569" allowDecimals={false} />
                    <Tooltip formatter={(value: number) => [`${value}`, "Shipment quantity"]} />
                    <Bar dataKey="count" name="Shipment quantity" fill="#F07E26" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {isTemperatureDashboard ? (
        <>
          {hasExcursionScatter ? (
            <section className="chart-panel">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "1rem",
                  marginBottom: "0.75rem",
                }}
              >
                <h3 style={{ margin: 0 }}>Excursion profile</h3>
                <div
                  style={{
                    display: "inline-flex",
                    borderRadius: "999px",
                    border: "1px solid #cbd5e1",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setExcursionYAxisMode("min")}
                    style={{
                      padding: "0.35rem 0.85rem",
                      border: "none",
                      cursor: "pointer",
                      backgroundColor: excursionYAxisMode === "min" ? "rgba(0, 115, 140, 0.12)" : "#ffffff",
                      color: excursionYAxisMode === "min" ? "#005566" : "#334155",
                      fontWeight: excursionYAxisMode === "min" ? 700 : 500,
                    }}
                  >
                    Min temp
                  </button>
                  <button
                    type="button"
                    onClick={() => setExcursionYAxisMode("max")}
                    style={{
                      padding: "0.35rem 0.85rem",
                      border: "none",
                      borderLeft: "1px solid #cbd5e1",
                      cursor: "pointer",
                      backgroundColor: excursionYAxisMode === "max" ? "rgba(0, 115, 140, 0.12)" : "#ffffff",
                      color: excursionYAxisMode === "max" ? "#005566" : "#334155",
                      fontWeight: excursionYAxisMode === "max" ? 700 : 500,
                    }}
                  >
                    Max temp
                  </button>
                </div>
              </div>
              <p className="muted" style={{ marginBottom: "0.75rem" }}>
                X axis: total shipment duration. Y axis: {excursionYAxisMode === "min" ? "minimum" : "maximum"} curve temperature.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", marginBottom: "0.75rem" }}>
                {(Object.keys(EXCURSION_SCATTER_LABELS) as ExcursionScatterCategory[]).map((category) => (
                  <span
                    key={category}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.4rem",
                      padding: "0.25rem 0.6rem",
                      borderRadius: "999px",
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      fontSize: "0.85rem",
                      color: "#334155",
                    }}
                  >
                    <span
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "999px",
                        background: EXCURSION_SCATTER_COLORS[category],
                      }}
                    />
                    {EXCURSION_SCATTER_LABELS[category]} ({excursionScatter.counts[category]})
                  </span>
                ))}
              </div>
              {hasBoxes && excursionScatter.packageLegend.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", marginBottom: "0.9rem" }}>
                  {excursionScatter.packageLegend.map((entry) => (
                    <span
                      key={entry.packageType}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.45rem",
                        padding: "0.2rem 0.55rem",
                        borderRadius: "999px",
                        background: "#ffffff",
                        border: "1px solid #e2e8f0",
                        fontSize: "0.82rem",
                        color: "#334155",
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                        <PackageShapeMarker shape={entry.shape} fill="#0f172a" x={7} y={7} />
                      </svg>
                      {entry.packageType}
                    </span>
                  ))}
                </div>
              ) : null}
              <ResponsiveContainer width="100%" height={360}>
                <ScatterChart margin={{ top: 8, right: 20, bottom: 20, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    type="number"
                    dataKey="durationHours"
                    name="Total duration"
                    unit=" h"
                    stroke="#475569"
                    allowDecimals={false}
                  />
                  <YAxis
                    type="number"
                    dataKey="plotTemperature"
                    name="Temperature"
                    unit="°C"
                    stroke="#475569"
                    domain={excursionYDomain}
                    tickFormatter={(value: number) => `${value}°C`}
                  />
                  <Tooltip content={<ExcursionScatterTooltip />} />
                  {safeRange ? (
                    <>
                      <ReferenceLine
                        y={safeRange.min}
                        stroke="#2563eb"
                        strokeDasharray="4 4"
                        label={{ value: "Cold threshold", fill: "#2563eb", fontSize: 12, position: "insideTopLeft" }}
                      />
                      <ReferenceLine
                        y={safeRange.max}
                        stroke="#fb7185"
                        strokeDasharray="4 4"
                        label={{ value: "Hot threshold", fill: "#fb7185", fontSize: 12, position: "insideTopRight" }}
                      />
                    </>
                  ) : null}
                  {(Object.keys(EXCURSION_SCATTER_LABELS) as ExcursionScatterCategory[]).map((category) => (
                    <Scatter
                      key={category}
                      name={EXCURSION_SCATTER_LABELS[category]}
                      data={excursionScatter.points.filter((point) => point.category === category)}
                      fill={EXCURSION_SCATTER_COLORS[category]}
                      shape={renderPackageScatterShape}
                    />
                  ))}
                </ScatterChart>
              </ResponsiveContainer>
            </section>
          ) : null}

          {hasSensorHeatmap ? (
            <SensorHeatmapPanel
              insideData={figures.heatmapInside ?? []}
              outsideData={figures.heatmapOutside ?? []}
              xLabel="Transport duration (%)"
              yLabel="Temperature (°C)"
            />
          ) : null}

          {hasExperienceHeatmap ? (
            <HeatmapPanel
              title="Shipping duration by destination country"
              dataset={experienceHeatmap}
              unit="h"
              emptyMessage="No shipping duration data."
              rowHeaderLabel="Country"
            />
          ) : null}

          {hasOutsideTemperatureHeatmap ? (
            <HeatmapPanel
              title="Température extérieure par destination (°C)"
              dataset={outsideTemperatureHeatmap}
              unit="°C"
              emptyMessage="Pas de relevés de température extérieure."
              rowHeaderLabel="Country"
            />
          ) : null}
        </>
      ) : null}

      {hasBoxDistributionChart || hasBoxExcursionRateChart ? (
        <section className="chart-panel">
          <h3>Packages and excursions</h3>
          {boxExcursionStackedData.length > 0 ? (
            <div style={{ minWidth: "320px" }}>
              <h4 style={{ marginBottom: "0.5rem" }}>Package breakdown and excursions</h4>
              <ResponsiveContainer width="100%" height={344}>
                <BarChart
                  data={boxExcursionStackedData}
                  layout="vertical"
                  margin={{ top: 8, right: 16, bottom: 28, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    type="number"
                    stroke="#475569"
                    allowDecimals={false}
                    label={{ value: "Quantity of package used", position: "bottom", offset: 8, fill: "#475569" }}
                  />
                  <YAxis
                    type="category"
                    dataKey="boxIdentifier"
                    width={220}
                    stroke="#475569"
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    formatter={(value: number, name: string, item) => {
                      const payload = item.payload as {
                        shipments: number;
                        withExcursion: number;
                        excursionRate: number;
                      };
                      if (name === "With excursion") {
                        return [`${value} item(s)`, `With excursion (${payload.excursionRate.toFixed(1)}%)`];
                      }
                      return [`${value} item(s)`, "Without excursion"];
                    }}
                    labelFormatter={(label) => `Package ${label}`}
                  />
                  <Legend verticalAlign="top" align="right" wrapperStyle={{ paddingBottom: "0.5rem" }} />
                  <Bar
                    dataKey="withoutExcursion"
                    name="Without excursion"
                    stackId="boxes"
                    fill={LAINPHARMA_BAR_BLUE}
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    dataKey="withExcursion"
                    name="With excursion"
                    stackId="boxes"
                    fill={EXCURSION_BAR_ORANGE}
                    radius={[0, 0, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

type PieDatum = { name: string; value: number };

type PieBlockProps = {
  title: string;
  data: PieDatum[];
  colors: string[];
  focusIndex?: number;
  percentage?: number;
  fraction?: { label: string; numerator: number; denominator: number };
  showSliceLabels?: boolean;
};

function PieBlock({ title, data, colors, focusIndex = 0, percentage, fraction, showSliceLabels }: PieBlockProps) {
  const total = data.reduce((acc, item) => acc + item.value, 0);
  const focusValue = total > 0 ? data[focusIndex]?.value ?? 0 : 0;
  const percentToDisplay = percentage ?? (total > 0 ? (focusValue / total) * 100 : 0);

  return (
    <div style={{ flex: "1 1 280px", minWidth: "260px" }}>
      <h4 style={{ marginBottom: "0.5rem" }}>{title}</h4>
      {total > 0 ? (
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius="55%"
              outerRadius="80%"
              paddingAngle={3}
              labelLine={false}
            >
              {data.map((entry, index) => (
                <Cell key={entry.name} fill={colors[index % colors.length]} />
              ))}
              {showSliceLabels ? (
                <LabelList
                  dataKey="value"
                  position="inside"
                  style={{ fontSize: "0.85rem", fontWeight: 600, fill: "#ffffff" }}
                  formatter={(value: number, _name: string, props) => {
                    const payload = (props as { payload?: PieDatum })?.payload;
                    const label = payload?.name ?? "";
                    return label ? `${label} ${value}` : `${value}`;
                  }}
                />
              ) : null}
              <Label
                value={`${percentToDisplay.toFixed(1)}%`}
                position="center"
                style={{ fontSize: "1.2rem", fontWeight: 700, fill: "#1f2937" }}
              />
            </Pie>
            <Tooltip formatter={(value: number, name: string) => [`${value}`, name]} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <p className="muted">Aucune donnée disponible.</p>
      )}
      {fraction ? (
        <p className="muted" style={{ marginTop: "0.5rem" }}>
          {fraction.label} : {fraction.numerator} / {fraction.denominator || 0}
        </p>
      ) : null}
    </div>
  );
}

type HeatmapPanelProps = {
  title: string;
  dataset: HeatmapDataset;
  unit: string;
  emptyMessage: string;
  rowHeaderLabel?: string;
};

type SensorHeatmapPanelProps = {
  insideData: SensorHeatmapCell[];
  outsideData: SensorHeatmapCell[];
  xLabel?: string;
  yLabel?: string;
};

type HeatmapMode = "inside" | "outside";

const LAINPHARMA_LIGHT_BLUE = BRAND_COLORS.blueLight;
const LAINPHARMA_LIGHT_BLUE_BORDER = hexToRgba(BRAND_COLORS.blueDark, 0.2);
const LAINPHARMA_LIGHT_BLUE_ACTIVE = hexToRgba(BRAND_COLORS.blueDark, 0.12);

function SensorHeatmapPanel({ insideData, outsideData, xLabel, yLabel }: SensorHeatmapPanelProps) {
  const [mode, setMode, hasInside, hasOutside] = useHeatmapMode(insideData, outsideData);
  const dataset = mode === "inside" ? insideData : outsideData;
  const hasData = dataset.length > 0;

  return (
    <section className="chart-panel" style={{ overflowX: "auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "0.75rem",
        }}
      >
        <h3 style={{ margin: 0 }}>
          {mode === "inside"
            ? "Heatmap of inside temperature vs transport duration"
            : "Heatmap of outside temperature vs transport duration"}
        </h3>
        <HeatmapModeSwitcher
          mode={mode}
          onChange={setMode}
          disabledInside={!hasInside}
          disabledOutside={!hasOutside}
        />
      </div>

      {hasData ? (
        <HeatmapGrid data={dataset} xLabel={xLabel} yLabel={yLabel} />
      ) : (
        <p className="muted">
          {mode === "inside" ? "No inside sensor data." : "No outside sensor data."}
        </p>
      )}
    </section>
  );
}

type HeatmapModeSwitcherProps = {
  mode: HeatmapMode;
  onChange: (mode: HeatmapMode) => void;
  disabledInside: boolean;
  disabledOutside: boolean;
};

function HeatmapModeSwitcher({ mode, onChange, disabledInside, disabledOutside }: HeatmapModeSwitcherProps) {
  return (
    <div
      style={{
        display: "inline-flex",
        borderRadius: "999px",
        border: `1px solid ${LAINPHARMA_LIGHT_BLUE_BORDER}`,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => onChange("inside")}
        disabled={disabledInside}
        style={{
          padding: "0.35rem 0.85rem",
          border: "none",
          cursor: disabledInside ? "not-allowed" : "pointer",
          backgroundColor: mode === "inside" ? LAINPHARMA_LIGHT_BLUE_ACTIVE : "transparent",
          color: mode === "inside" ? "#005566" : "#1f2937",
          fontWeight: mode === "inside" ? 600 : 500,
          opacity: disabledInside ? 0.5 : 1,
        }}
      >
        Inside temperature
      </button>
      <button
        type="button"
        onClick={() => onChange("outside")}
        disabled={disabledOutside}
        style={{
          padding: "0.35rem 0.85rem",
          border: "none",
          cursor: disabledOutside ? "not-allowed" : "pointer",
          backgroundColor: mode === "outside" ? LAINPHARMA_LIGHT_BLUE_ACTIVE : "transparent",
          color: mode === "outside" ? "#005566" : "#1f2937",
          fontWeight: mode === "outside" ? 600 : 500,
          opacity: disabledOutside ? 0.5 : 1,
        }}
      >
        Outside temperature
      </button>
    </div>
  );
}

function useHeatmapMode(
  insideData: SensorHeatmapCell[],
  outsideData: SensorHeatmapCell[],
): [HeatmapMode, (mode: HeatmapMode) => void, boolean, boolean] {
  const hasInside = insideData.length > 0;
  const hasOutside = outsideData.length > 0;
  const defaultMode: HeatmapMode = hasInside ? "inside" : "outside";
  const [mode, setMode] = useState<HeatmapMode>(defaultMode);

  useEffect(() => {
    if (mode === "inside" && !hasInside && hasOutside) {
      setMode("outside");
    }
    if (mode === "outside" && !hasOutside && hasInside) {
      setMode("inside");
    }
  }, [mode, hasInside, hasOutside]);

  const updateMode = (next: HeatmapMode) => {
    if (next === "inside" && !hasInside) return;
    if (next === "outside" && !hasOutside) return;
    setMode(next);
  };

  return [mode, updateMode, hasInside, hasOutside];
}

type HeatmapGridProps = {
  data: SensorHeatmapCell[];
  xLabel?: string;
  yLabel?: string;
};

function HeatmapGrid({ data, xLabel, yLabel }: HeatmapGridProps) {
  const percentages = Array.from(new Set(data.map((cell) => cell.percentage))).sort((a, b) => a - b);
  const temperatures = Array.from(new Set(data.map((cell) => cell.temperature))).sort((a, b) => b - a);
  const maxCount = Math.max(...data.map((cell) => cell.count), 0);
  const cellMap = new Map<string, number>();
  data.forEach((cell) => {
    cellMap.set(`${cell.percentage.toFixed(2)}|${cell.temperature}`, cell.count);
  });

  const gridTemplateColumns = `88px repeat(${percentages.length}, minmax(54px, 1fr))`;

  return (
    <>
      <p className="muted" style={{ marginBottom: "0.75rem" }}>
        {yLabel ?? "Temperature (°C)"} vs {xLabel ?? "Transport duration (%)"}
      </p>
      <div style={{ minWidth: 88 + percentages.length * 60, paddingBottom: "0.5rem" }}>
        <div style={{ display: "grid", gap: "6px", gridTemplateColumns }}>
          <div style={{ ...heatmapHeaderStyle, background: "transparent" }} />
          {percentages.map((percentage) => (
            <div key={`header-${percentage}`} style={heatmapHeaderStyle}>
              {(percentage * 100).toFixed(0)}%
            </div>
          ))}

          {temperatures.map((temperature) => (
            <Fragment key={`row-${temperature}`}>
              <div style={heatmapDestinationStyle}>{`${temperature} °C`}</div>
              {percentages.map((percentage) => {
                const key = `${percentage.toFixed(2)}|${temperature}`;
                const count = cellMap.get(key) ?? 0;
                if (count <= 0) {
                  return (
                    <div key={key} style={heatmapEmptyStyle}>
                      0
                    </div>
                  );
                }
                const ratio = maxCount > 0 ? count / maxCount : 0;
                const background = countColor(count, maxCount);
                const textColor = ratio > 0.55 ? "#ffffff" : "#0f172a";

                return (
                  <div key={key} style={{ ...heatmapCellStyle, background, color: textColor }}>
                    {count}
                  </div>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>

      <div style={{ marginTop: "0.75rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span className="muted" style={{ fontSize: "0.8rem" }}>
            0
          </span>
          <div
            style={{
              flex: "1 1 auto",
              height: "10px",
              borderRadius: "999px",
              background: "linear-gradient(90deg, #f8fafc 0%, #fb923c 100%)",
            }}
          />
          <span className="muted" style={{ fontSize: "0.8rem" }}>
            {maxCount}
          </span>
        </div>
        <p className="muted" style={{ marginTop: "0.35rem" }}>
          Intensity based on the number of recorded measurements.
        </p>
      </div>
    </>
  );
}

function HeatmapPanel({ title, dataset, unit, emptyMessage, rowHeaderLabel = "Destination" }: HeatmapPanelProps) {
  const hasData = dataset.rows.length > 0 && dataset.domain;

  return (
    <section className="chart-panel">
      <h3>{title}</h3>
      {hasData && dataset.domain ? (
        <>
          <div style={{ paddingRight: "0.5rem" }}>
            <div
              style={{
                display: "grid",
                gap: "4px",
                gridTemplateColumns: `180px repeat(${HEATMAP_METRICS.length}, minmax(110px, 1fr))`,
              }}
            >
              <div style={heatmapHeaderStyle}>{rowHeaderLabel}</div>
              {HEATMAP_METRICS.map((metric) => (
                <div key={metric.key} style={heatmapHeaderStyle}>
                  {metric.label}
                </div>
              ))}

              {dataset.rows.map((row) => (
                <Fragment key={row.destination}>
                  <div style={heatmapDestinationStyle}>{row.destination}</div>
                  {HEATMAP_METRICS.map((metric) => {
                    const value = row.values[metric.key];
                    if (!isFiniteNumber(value)) {
                      return (
                        <div key={metric.key} style={heatmapEmptyStyle}>
                          —
                        </div>
                      );
                    }

                    const color = heatColor(value, dataset.domain![0], dataset.domain![1]);
                    const ratio =
                      dataset.domain![1] > dataset.domain![0]
                        ? (value - dataset.domain![0]) / (dataset.domain![1] - dataset.domain![0])
                        : 0;
                    const textColor = ratio > 0.55 ? "#ffffff" : "#0f172a";

                    return (
                      <div
                        key={metric.key}
                        style={{ ...heatmapCellStyle, background: color, color: textColor }}
                      >
                        {`${value.toFixed(1)} ${unit}`}
                      </div>
                    );
                  })}
                </Fragment>
              ))}
            </div>
          </div>
          <p className="muted" style={{ marginTop: "0.75rem" }}>
            Plage observée : {dataset.domain[0].toFixed(1)} à {dataset.domain[1].toFixed(1)} {unit}
          </p>
        </>
      ) : (
        <p className="muted">{emptyMessage}</p>
      )}
    </section>
  );
}

const heatmapHeaderStyle: CSSProperties = {
  fontWeight: 600,
  fontSize: "0.85rem",
  padding: "0.45rem 0.55rem",
  background: LAINPHARMA_LIGHT_BLUE,
  borderRadius: "6px",
};

const heatmapCellStyle: CSSProperties = {
  padding: "0.65rem 0.85rem",
  borderRadius: "6px",
  textAlign: "center",
  fontSize: "0.85rem",
  fontWeight: 600,
};

const heatmapDestinationStyle: CSSProperties = {
  ...heatmapCellStyle,
  background: LAINPHARMA_LIGHT_BLUE,
  textAlign: "left",
  fontWeight: 600,
  color: "#1f2937",
  padding: "0.55rem 0.6rem",
  fontSize: "0.8rem",
};

const heatmapEmptyStyle: CSSProperties = {
  ...heatmapCellStyle,
  background: "#f8fafc",
  color: "#94a3b8",
  fontWeight: 500,
};

function buildHeatmapDataset(
  source: Array<{ destination: string; min: number | null; max: number | null; mean: number | null }>,
): HeatmapDataset {
  if (!source.length) return { rows: [], domain: null };

  const sorted = [...source].sort((a, b) => (b.mean ?? 0) - (a.mean ?? 0));

  const rows: HeatmapRow[] = sorted.map((entry) => ({
    destination: entry.destination ?? "N/A",
    values: {
      min: isFiniteNumber(entry.min) ? entry.min : null,
      mean: isFiniteNumber(entry.mean) ? entry.mean : null,
      max: isFiniteNumber(entry.max) ? entry.max : null,
    },
  }));

  const numericValues = rows
    .flatMap((row) => HEATMAP_METRICS.map((metric) => row.values[metric.key]))
    .filter((value): value is number => isFiniteNumber(value));

  if (!numericValues.length) {
    return { rows: [], domain: null };
  }

  const min = Math.min(...numericValues);
  const max = Math.max(...numericValues);

  return { rows, domain: [min, max] };
}

function heatColor(value: number, min: number, max: number) {
  if (max <= min) {
    return "rgba(0, 115, 140, 0.22)";
  }
  const ratio = (value - min) / (max - min);
  const hue = 196 - ratio * 18;
  const saturation = 48 + ratio * 12;
  const lightness = 84 - ratio * 24;
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

function countColor(count: number, max: number) {
  if (max <= 0 || count <= 0) return "#f8fafc";
  const ratio = count / max;
  const hue = 28;
  const saturation = 92;
  const lightness = 96 - ratio * 56;
  return `hsl(${hue}, ${saturation}%, ${Math.max(lightness, 32)}%)`;
}

function ratioToPercent(part: number, total: number) {
  if (!total || !Number.isFinite(part) || !Number.isFinite(total)) return 0;
  return (part / total) * 100;
}

function isFiniteNumber(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function buildExcursionScatterData(
  shipments: ShipmentSummary[],
  safeRange: { min: number; max: number } | null,
  yAxisMode: ExcursionYAxisMode,
  hasBoxes: boolean,
): {
  points: ExcursionScatterPoint[];
  counts: Record<ExcursionScatterCategory, number>;
  packageLegend: Array<{ packageType: string; shape: PackageShape }>;
} {
  const counts: Record<ExcursionScatterCategory, number> = {
    none: 0,
    cold: 0,
    hot: 0,
    mixed: 0,
  };

  const packageShapeMap = new Map<string, PackageShape>();

  const points = shipments.flatMap<ExcursionScatterPoint>((shipment) => {
    const durationHours = shipment.totalHours;
    if (!isFiniteNumber(durationHours) || durationHours <= 0) return [];

    const devices = pickRelevantDevices(shipment);
    const minValue = devices
      .map((device) => device.minValue)
      .filter(isFiniteNumber)
      .reduce<number | null>((acc, value) => (acc === null ? value : Math.min(acc, value)), null);
    const maxValue = devices
      .map((device) => device.maxValue)
      .filter(isFiniteNumber)
      .reduce<number | null>((acc, value) => (acc === null ? value : Math.max(acc, value)), null);

    const packageType = hasBoxes ? resolvePackageType(shipment.boxIdentifier) : "Shipment";
    const packageShape = resolvePackageShape(packageType, packageShapeMap);
    const category = classifyExcursion(minValue, maxValue, safeRange);
    const plotTemperature = yAxisMode === "min" ? minValue : maxValue;
    if (!isFiniteNumber(plotTemperature)) return [];
    counts[category] += 1;

    return [
      {
        packageLabel: hasBoxes ? resolvePackageLabel(shipment.boxIdentifier) : shipment.id,
        packageType,
        packageShape,
        durationHours: Number(durationHours.toFixed(2)),
        plotTemperature: Number(plotTemperature.toFixed(2)),
        minValue,
        maxValue,
        category,
      },
    ];
  });

  const packageLegend = Array.from(packageShapeMap.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([packageType, shape]) => ({ packageType, shape }));

  return { points, counts, packageLegend };
}

function pickRelevantDevices(shipment: ShipmentSummary) {
  const insideDevices = shipment.devices.filter((device) => (device.position ?? "").toUpperCase() === "INSIDE");
  return insideDevices.length > 0 ? insideDevices : shipment.devices;
}

function classifyExcursion(
  minValue: number | null,
  maxValue: number | null,
  safeRange: { min: number; max: number } | null,
): ExcursionScatterCategory {
  if (!safeRange) return "none";
  const hasCold = isFiniteNumber(minValue) && minValue < safeRange.min;
  const hasHot = isFiniteNumber(maxValue) && maxValue > safeRange.max;
  if (hasCold && hasHot) return "mixed";
  if (hasHot) return "hot";
  if (hasCold) return "cold";
  return "none";
}

function resolvePackageLabel(boxIdentifier: string | null): string {
  if (!boxIdentifier) return "—";
  const first = boxIdentifier
    .split(",")
    .map((value) => value.trim())
    .find(Boolean);
  return first ?? "—";
}

function resolvePackageType(boxIdentifier: string | null): string {
  if (!boxIdentifier) return "Unknown";
  const first = boxIdentifier
    .split(",")
    .map((value) => value.trim())
    .find(Boolean);
  if (!first) return "Unknown";
  const parts = first
    .split("-")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
  return (parts[parts.length - 1] ?? first).toUpperCase();
}

function resolvePackageShape(packageType: string, shapeMap: Map<string, PackageShape>): PackageShape {
  const existing = shapeMap.get(packageType);
  if (existing) return existing;
  const shape = PACKAGE_SHAPES[shapeMap.size % PACKAGE_SHAPES.length] ?? "circle";
  shapeMap.set(packageType, shape);
  return shape;
}

function buildExcursionYAxisDomain(
  points: ExcursionScatterPoint[],
  safeRange: { min: number; max: number } | null,
  configuredDomain: readonly [number, number] | null,
): [number, number] {
  if (
    configuredDomain &&
    Number.isFinite(configuredDomain[0]) &&
    Number.isFinite(configuredDomain[1]) &&
    configuredDomain[0] < configuredDomain[1]
  ) {
    return [configuredDomain[0], configuredDomain[1]];
  }

  const values = points.map((point) => point.plotTemperature).filter((value) => Number.isFinite(value));
  const safeMin = safeRange?.min;
  const safeMax = safeRange?.max;
  const baseMin = Math.min(
    ...(values.length ? values : [safeMin ?? 0, safeMax ?? 0]),
    ...(typeof safeMin === "number" ? [safeMin] : []),
  );
  const baseMax = Math.max(
    ...(values.length ? values : [safeMin ?? 0, safeMax ?? 0]),
    ...(typeof safeMax === "number" ? [safeMax] : []),
  );
  const padding = Math.max(1, (baseMax - baseMin) * 0.08);
  return [Math.floor(baseMin - padding), Math.ceil(baseMax + padding)];
}

type PackageScatterShapeProps = {
  cx?: number;
  cy?: number;
  fill?: string;
  payload?: ExcursionScatterPoint;
};

function renderPackageScatterShape(props: PackageScatterShapeProps) {
  const { cx = 0, cy = 0, fill = "#0f172a", payload } = props;
  const shape = payload?.packageShape ?? "circle";
  return <PackageShapeMarker shape={shape} fill={fill} x={cx} y={cy} />;
}

type PackageShapeMarkerProps = {
  shape: PackageShape;
  fill: string;
  x: number;
  y: number;
};

function PackageShapeMarker({ shape, fill, x, y }: PackageShapeMarkerProps) {
  const size = 5;
  const common: SVGProps<SVGElement> = {
    fill,
    stroke: fill,
    strokeWidth: 1,
  };

  if (shape === "square") {
    return <rect x={x - size} y={y - size} width={size * 2} height={size * 2} {...common} />;
  }
  if (shape === "diamond") {
    return <path d={`M ${x} ${y - size} L ${x + size} ${y} L ${x} ${y + size} L ${x - size} ${y} Z`} {...common} />;
  }
  if (shape === "triangle") {
    return <path d={`M ${x} ${y - size} L ${x + size} ${y + size} L ${x - size} ${y + size} Z`} {...common} />;
  }
  if (shape === "cross") {
    return (
      <path
        d={`M ${x - size} ${y - 1.5} H ${x - 1.5} V ${y - size} H ${x + 1.5} V ${y - 1.5} H ${x + size} V ${y + 1.5} H ${x + 1.5} V ${y + size} H ${x - 1.5} V ${y + 1.5} H ${x - size} Z`}
        {...common}
      />
    );
  }
  if (shape === "star") {
    return (
      <path
        d={`M ${x} ${y - size} L ${x + 1.8} ${y - 1.8} L ${x + size} ${y - 1.4} L ${x + 2.6} ${y + 0.8} L ${x + 3.2} ${y + size} L ${x} ${y + 2.3} L ${x - 3.2} ${y + size} L ${x - 2.6} ${y + 0.8} L ${x - size} ${y - 1.4} L ${x - 1.8} ${y - 1.8} Z`}
        {...common}
      />
    );
  }
  return <circle cx={x} cy={y} r={size} {...common} />;
}

type ExcursionScatterTooltipProps = {
  active?: boolean;
  payload?: Array<{ payload: ExcursionScatterPoint }>;
};

function ExcursionScatterTooltip({ active, payload }: ExcursionScatterTooltipProps) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  return (
    <div
      style={{
        background: "rgba(15, 23, 42, 0.94)",
        color: "#e2e8f0",
        borderRadius: "10px",
        padding: "0.75rem 0.9rem",
        minWidth: "220px",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.25)",
      }}
    >
      <div style={{ fontWeight: 700 }}>{point.packageLabel}</div>
    </div>
  );
}
